import { AppRoutes } from "constants/routes";

// Both standard reports are for a single site. On a page with a site context the chips say "this
// site"; elsewhere the generic phrasing lets the assistant ask which site (or look one up). Chips
// are suggestions only — the backend applies the caller's real access to whatever they click.
const REPORT_PROMPTS = {
  siteIndexThisSite: {
    label: "Site index for this site",
    message: "Generate a site index report for this site",
  },
  activityThisSite: {
    label: "Activity report for this site",
    message: "Generate an activity report for this site",
  },
  siteIndex: { label: "Generate a site index report" },
  activity: { label: "Generate an activity report" },
};

const DEFAULT_PROMPTS = [
  REPORT_PROMPTS.siteIndex,
  REPORT_PROMPTS.activity,
  { label: "What's been happening in my sites this week?" },
];

const matchers = [
  {
    test: (path) => new RegExp(`^${AppRoutes.Sites}/.+`).test(path),
    prompts: [
      REPORT_PROMPTS.siteIndexThisSite,
      REPORT_PROMPTS.activityThisSite,
      { label: "What's been happening on this site this week?" },
    ],
  },
  {
    test: (path) => new RegExp(`^${AppRoutes.Workspace}/.+`).test(path),
    prompts: [
      REPORT_PROMPTS.siteIndexThisSite,
      REPORT_PROMPTS.activityThisSite,
      { label: "Which documents here changed recently?" },
    ],
  },
  {
    test: (path) => path === AppRoutes.Sites,
    prompts: [
      REPORT_PROMPTS.siteIndex,
      REPORT_PROMPTS.activity,
      { label: "Which sites had the most downloads last month?" },
    ],
  },
  {
    // A single user's profile — the assistant knows which user from the page URL
    test: (path) => new RegExp(`^${AppRoutes.Users}/.+`).test(path),
    prompts: [
      { label: "What has this user done this month?" },
      { label: "Which sites can this user access?" },
      { label: "Which documents has this user downloaded recently?" },
    ],
  },
  {
    // The users admin area is CanViewUsers-gated, so user-centric ad-hoc reports fit here
    test: (path) => path === AppRoutes.Users,
    prompts: [
      { label: "Generate a user list report" },
      {
        label: "Users active per day this month",
        message: "Generate a report of users active per day this month",
      },
      { label: "Who hasn't signed in for a month?" },
    ],
  },
  {
    test: (path) => new RegExp(`^${AppRoutes.Groups}/.+`).test(path),
    prompts: [
      { label: "Who is in this group?" },
      { label: "Which folders and documents are in this group's scope?" },
      REPORT_PROMPTS.activity,
    ],
  },
  {
    test: (path) => path === AppRoutes.Groups,
    prompts: [
      { label: "Which groups grant access to each site?" },
      { label: "Which groups have the most members?" },
      {
        label: "Group membership report",
        message: "Generate a report of groups and their members",
      },
    ],
  },
  {
    test: (path) => path === AppRoutes.Roles,
    prompts: [
      { label: "Who holds each role?" },
      {
        label: "Users and roles report",
        message: "Generate a report of users and the roles they hold",
      },
      REPORT_PROMPTS.siteIndex,
    ],
  },
  {
    test: (path) => path === AppRoutes.Organisations,
    prompts: [
      { label: "How many users does each organisation have?" },
      {
        label: "Users by organisation report",
        message: "Generate a report of users grouped by organisation",
      },
      { label: "Which organisations were active this month?" },
    ],
  },
  {
    test: (path) => path === AppRoutes.Bin,
    prompts: [
      { label: "What was deleted recently?" },
      { label: "Who deleted the most documents this month?" },
      REPORT_PROMPTS.activity,
    ],
  },
  {
    test: (path) => path === AppRoutes.Root,
    prompts: [
      { label: "What's been happening in my sites this week?" },
      { label: "Which documents changed recently?" },
      REPORT_PROMPTS.siteIndex,
    ],
  },
];

/**
 * Suggested prompt chips for the Aide widget's empty state, tailored to the page the user is on.
 * Passed to mount() as the suggestedPrompts provider and re-evaluated on navigation.
 */
export const getSuggestedPrompts = (pageUrl) => {
  const path = pageUrl || "/";
  const match = matchers.find((m) => m.test(path));
  return match ? match.prompts : DEFAULT_PROMPTS;
};
